import { DOUDIAN_SE_APP_KEY } from "@/config";
import ShopType from "@/enum/ShopType";
import { MCToken, getMcToken } from "@/services/api/mcToken";
import GlobalStore from "@/store";
import { loadRemoteScript } from "@/utils/scriptLoader";
import to from "@/utils/to";
import { computed, observable, when, makeObservable, actionBound } from "mobx";
import moment from "moment";

const McTokenCachePrefix = "__McTokenCache__";

function buildMcTokenCacheKey(shopId: number) {
    return McTokenCachePrefix + shopId;
}

/**
 * 处理抖店SDK任务
 * 1.抖店解密收货人
 * 2.抖店消息通知 https://bytedance.larkoffice.com/docx/MRROdoTO5ouRPdx2vcscYrfYnLc
 * 3.批量改价 https://www.tapd.cn/22185411/prong/tasks/view/1122185411001004694?url_cache_key=from_url_task_list_0b7a4f69b2951d17f49d528a107c98f2&action_entry_type=task_list
 * 4.报备700虚拟号 https://bytedance.larkoffice.com/docx/WT2Rdo6ETo6wn1x9Ia9cn5lxnmh
 *
 */
class DouDianSDKStore {
    /** 仅百分之5用户可以有机会展示抖店弹窗组件 */
    get allowShowComponent() {
        if ([175712, 804329].includes(this.globalStore.currentUser?.shopId)) {
            return true;
        }

        if (Math.random() < 0.05) {
            return true;
        }

        return false;
    }

    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            allowShowComponent: computed,
            scriptLoading: observable,
            mcTokenMapping: observable,
            mcTokenLoadingMapping: observable,
            isDouDianUser: computed,
            isInit: observable,
            init: actionBound,
            currentUserShopId: computed,
            loadScripts: actionBound,
            initRealBuyerUsername: actionBound,
            init700ReportCmp: actionBound,
            getMcTokenFromCache: actionBound,
            loadMcToken: actionBound,
            ensureMcToken: actionBound,
        });
    }

    scriptLoading = false;

    loadScriptPromise: Promise<void> | undefined;

    mcTokenMapping = new Map<number, MCToken>();

    mcTokenLoadingMapping = new Map<number, boolean>();

    get isDouDianUser() {
        const shopMapping = this.globalStore.shopMapping;
        const currentUserShopId = this.globalStore.currentUser?.shopId;
        const currentUserShop = shopMapping.get(currentUserShopId);

        return currentUserShop?.type === ShopType.DOUDIAN;
    }

    isInit = false;

    async init() {
        try {
            await when(() => this.globalStore.isInit);

            if (!this.isDouDianUser) return;

            await this.loadScripts();

            await this.loadMcToken(this.currentUserShopId);
        } catch {}

        this.isInit = true;
    }

    get currentUserShopId() {
        return this.globalStore.currentUser?.shopId;
    }

    async loadScripts(validateCurrentUser: boolean = true) {
        await when(() => this.globalStore.isInit);

        if (validateCurrentUser) {
            if (!this.isDouDianUser) return;
        }

        if (this.loadScriptPromise) {
            return this.loadScriptPromise;
        }

        try {
            this.scriptLoading = true;
            this.loadScriptPromise = await loadRemoteScript(
                "https://lf1-fe.ecombdstatic.com/obj/ecom-fe-open-public/component/index.js",
            );
        } catch {}

        this.scriptLoading = false;
    }

    async initRealBuyerUsername(params: {
        element: HTMLElement;
        shopId: number;
        orderOriginalId: string;
        onStart?: () => void;
        onSuccess?: () => void;
        onError?: () => void;
    }) {
        const { element, shopId, orderOriginalId, onStart, onSuccess, onError } = params;

        await this.loadScripts(false);

        await this.ensureMcToken(shopId);

        const token = this.mcTokenMapping.get(shopId)?.token;
        const shop = this.globalStore.shopMapping.get(shopId);

        ecopen.start({
            token: token,
            appId: DOUDIAN_SE_APP_KEY,
            shopId: shop.sellerUsername,
            blockedDistributeComponents: [381],
        });

        ecopen.bixi(element, {
            token: token,
            appId: DOUDIAN_SE_APP_KEY,
            componentId: 414,
            shopId: shop.sellerUsername,
            onStart: () => {
                onStart?.();
            },
            onSuccess: () => {
                onSuccess?.();
            },
            onError: (error) => {
                onError?.();
            },
            // @ts-ignore
            extra: {
                orderId: orderOriginalId,
            },
        });
    }

    async init700ReportCmp(params: {
        element: HTMLElement;
        shopId: number;
        orderOriginalId: string;
        onStart?: () => void;
        onSuccess?: () => void;
        onError?: () => void;
    }) {
        const { element, shopId, orderOriginalId, onStart, onSuccess, onError } = params;

        await this.loadScripts(false);

        await this.ensureMcToken(shopId);

        const token = this.mcTokenMapping.get(shopId)?.token;
        const shop = this.globalStore.shopMapping.get(shopId);

        ecopen.start({
            token: token,
            appId: DOUDIAN_SE_APP_KEY,
            shopId: shop.sellerUsername,
            blockedDistributeComponents: [381],
        });

        ecopen.bixi(element, {
            token: token,
            appId: DOUDIAN_SE_APP_KEY,
            componentId: 507,
            shopId: shop.sellerUsername,
            onStart: () => {
                onStart?.();
            },
            onSuccess: () => {
                onSuccess?.();
            },
            onError: (error) => {
                onError?.();
            },
        });
    }

    /**
     * 从缓存获取McToken 如果Token过期则返回空
     */
    getMcTokenFromCache(shopId: number) {
        const cache = localStorage.getItem(buildMcTokenCacheKey(shopId));

        if (!cache) return undefined;

        const mcToken: MCToken = JSON.parse(cache);

        if (mcToken && mcToken.expireIn) {
            /** 前端提前半小时失效 */
            if (moment().add(30, "minute").isAfter(moment(mcToken.expireIn))) {
                return undefined;
            }

            return mcToken;
        }

        return undefined;
    }

    /**
     * 从接口获取McToken
     */
    async loadMcToken(shopId: number) {
        this.mcTokenLoadingMapping.set(shopId, true);

        const [, mcToken] = await to(getMcToken(shopId, { hideModal: true }));

        if (mcToken) {
            this.mcTokenMapping.set(shopId, mcToken);

            localStorage.setItem(buildMcTokenCacheKey(shopId), JSON.stringify(mcToken));
        }

        this.mcTokenLoadingMapping.set(shopId, false);

        return mcToken;
    }

    /** 加载McToken，首先尝试从缓存中提取，如无缓存则从接口获取 */
    async ensureMcToken(shopId: number) {
        const shop = this.globalStore.shopMapping.get(shopId);
        if (shop?.type !== ShopType.DOUDIAN) return;

        const loading = this.mcTokenLoadingMapping.get(shopId);

        if (loading) {
            await when(() => !this.mcTokenLoadingMapping.get(shopId));

            return;
        }

        let mcToken = this.getMcTokenFromCache(shopId);
        if (mcToken) {
            this.mcTokenMapping.set(shopId, mcToken);
        } else {
            await this.loadMcToken(shopId);
        }
    }
}

export default DouDianSDKStore;
